import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { connect } from 'react-redux';
import { getFormSyncErrors, getFormValues, isValid, reduxForm } from 'redux-form';
import { bindActionCreators, compose } from 'redux';
import { get } from 'lodash';
import getIncludedResource from '../../../../helpers/getIncludedResource';
import './PersonalAccidentForm.scss';
import { DETAILS_TAB, PAY_TAB, QUOTE_TAB } from './tabs';
import { actions as channelActions } from '../../../channel/redux/channelActions';
import QuoteTabsContainer from './components/QuoteTabsContainer/QuoteTabsContainer';
import Sidebar from './components/Sidebar/Sidebar';
import { isBroker } from '../../../auth/redux/authActions';

const INITIAL_INSURED = {
  first_name: '',
  last_name: '',
  sanction_id: '',
  dob: '',
};

const FORM_NAME = 'PersonalAccidentForm';

const PersonalAccidentForm = ({
  productCode,
  productId,
  dispatch,
  change,
  isMta,
  calculatedPremiums,
  handleSubmit,
  isCalculating,
  isSaving,
  formValues,
  directDebitQuestions,
  source,
  calculate,
  calculateMta,
  purchaseDeclaration,
  saveAndExit,
  saveEmailAndExit,
  save,
  issue,
  submitting,
  issuingError,
  productName,
  formErrors,
  submitFailed,
  rules,
}) => {
  const [activeTab, setActiveTab] = useState(1);
  const [finalPremium, setFinalPremium] = useState(null);
  const sourceId = formValues && formValues.data.relationships.source.data.id;
  const groupType = formValues && formValues.data.attributes.metadata.group_type;
  const numChildren = formValues && formValues.data.attributes.metadata.num_children;
  const existingAdults = formValues && formValues.data.attributes.metadata.adults;
  const existingChildren = formValues && formValues.data.attributes.metadata.children;
  const numExistingChildren =
    formValues && formValues.data.attributes.metadata.children.length;
  const campaignCode = formValues && formValues.data.attributes.metadata.campaign;

  const updateSource = useMemo(
    () => (newSource) => {
      if (!isBroker()) {
        dispatch(change('data.relationships.source.data.id', newSource.id));
        dispatch(
          change(
            'data.relationships.channel.data.id',
            get(newSource, 'relationships.channel.data.id', ''),
          ),
        );
        if (newSource.attributes.is_broker) {
          dispatch(
            change(
              'data.relationships.broker.data.id',
              newSource.relationships.broker.data.id,
            ),
          );
          dispatch(change('data.attributes.metadata.sales_channel', 'jtc-b2b'));
        } else {
          dispatch(change('data.relationships.broker.data.id', ''));
          dispatch(change('data.attributes.metadata.sales_channel', ''));
        }
      }
    },
    [dispatch, change],
  );

  useEffect(
    () => {
      if (source && source.id !== sourceId) {
        updateSource(source);
      }
    },
    [updateSource, source, sourceId],
  );

  useEffect(
    () => {
      if (groupType === 'individual') {
        dispatch(change('data.attributes.metadata.num_children', 0));
        dispatch(change('data.attributes.metadata.children', []));
        if (existingAdults.length > 1) {
          dispatch(change('data.attributes.metadata.adults', [existingAdults[0]]));
        }
      }
      if (groupType === 'couple') {
        if (existingAdults.length === 1) {
          const newAdults = [existingAdults[0], INITIAL_INSURED];
          dispatch(change('data.attributes.metadata.adults', newAdults));
        }
        dispatch(change('data.attributes.metadata.num_children', 0));
        dispatch(change('data.attributes.metadata.children', []));
      }
      if (groupType === 'spf') {
        if (existingAdults.length > 1) {
          const newAdults = [existingAdults[0]];
          dispatch(change('data.attributes.metadata.adults', newAdults));
        }
      }
      if (groupType === 'family') {
        if (existingAdults.length === 1) {
          const newAdults = [existingAdults[0], INITIAL_INSURED];
          dispatch(change('data.attributes.metadata.adults', newAdults));
        }
      }
    },
    [groupType, dispatch, change, existingAdults],
  );

  useEffect(
    () => {
      if (numChildren >= 0 && numChildren !== numExistingChildren) {
        if (!numExistingChildren || numChildren > numExistingChildren) {
          const children = [...Array(numChildren)].map(() => INITIAL_INSURED);
          const newChildren = children.fill(
            INITIAL_INSURED,
            numExistingChildren,
            numChildren,
          );
          dispatch(change('data.attributes.metadata.children', newChildren));
        } else {
          existingChildren.splice(0, numExistingChildren - numChildren);
        }
      }
    },
    [numChildren, numExistingChildren, dispatch, change, existingChildren],
  );

  useEffect(
    () => {
      if (campaignCode || campaignCode === '') {
        calculateQuotes();
      }
    },
    [campaignCode],
  );

  if (!formValues) {
    return null;
  }

  const calculateQuotes = () => {
    if (isMta) {
      dispatch(handleSubmit(calculateMta));
    } else {
      dispatch(handleSubmit(calculate));
    }
  };
  const saveApplicationAndGoToPayment = (values) => {
    const selectedSchemes = values.data.relationships.schemes.data;
    if (selectedSchemes.length === 1) {
      return Promise.resolve(save(values, true)).then((resp) => {
        const premiums = getIncludedResource(resp.data, resp.included, 'premiums');
        // if (resp.data.attributes.valid) {
        dispatch(change('data.id', resp.data.id));
        if (premiums && premiums.length === 1) {
          const premium = premiums[0];
          setFinalPremium(premium);
          dispatch(change('data.relationships.premium.data.id', premium.id));
          dispatch(change('meta.amount', premium.attributes.gross));
          setActiveTab(PAY_TAB);
        }
      });
    }
    return null;
  };

  const handlePaymentTab = () => {
    dispatch(handleSubmit(saveApplicationAndGoToPayment));
  };

  const handleTab = (key) => {
    if (key === PAY_TAB) {
      handlePaymentTab();
    } else {
      if (key === QUOTE_TAB) {
        calculateQuotes();
      }
      dispatch(change('data.current_tab', key));
      setActiveTab(key);
    }
  };

  const handleNextTab = () => {
    window.scrollTo(0, 0);
    handleTab(activeTab + 1);
  };

  const handlePreviousTab = () => {
    handleTab(activeTab - 1);
  };

  const validateAndGoToNextTab = () => {
    dispatch(handleSubmit(handleNextTab));
  };

  const handleIssuePolicy = () => {
    dispatch(handleSubmit(issue));
  };

  const navigateToTab = (key) => {
    if (key <= activeTab) {
      handleTab(key);
    } else {
      dispatch(handleSubmit(handleNextTab));
    }
  };

  const handleCommissionAdjustments = () => {
    calculateQuotes();
  };

  const handleSaveAndExit = () => {
    saveAndExit(formValues);
  };

  const handleEmailSaveAndExit = () => {
    saveEmailAndExit(formValues);
  };

  const paymentQuestions =
    formValues.data?.attributes?.metadata?.payment_declarations ??
    formValues.data?.attributes?.metadata?.direct_debit_payment_declaration_answers;
  return (
    <div className={' personal-accident-form'}>
      <Form horizontal>
        <QuoteTabsContainer
          productCode={productCode}
          productId={productId}
          isSaving={isCalculating || submitting}
          isMta={isMta}
          handleTab={navigateToTab}
          handleNextTab={validateAndGoToNextTab}
          handlePreviousTab={handlePreviousTab}
          handleSaveAndExit={handleSaveAndExit}
          handleCalculateQuotes={calculateQuotes}
          activeTab={activeTab}
          groupType={formValues.data.attributes.metadata.group_type}
          formValues={formValues}
          calculatedPremiums={calculatedPremiums}
          purchaseDeclaration={purchaseDeclaration}
          directDebitQuestions={directDebitQuestions}
          handleIssuePolicy={handleIssuePolicy}
          finalPremium={finalPremium}
          issuingError={issuingError}
          paymentDeclarations={paymentQuestions}
          rules={rules}
        />
        <Sidebar
          application={formValues}
          productName={productName}
          isMta={isMta}
          formErrors={formErrors}
          submitFailed={submitFailed}
          source={source}
          calculatedPremiums={calculatedPremiums}
          selectedSchemes={formValues.data.relationships.schemes.data}
          showSave={activeTab > DETAILS_TAB}
          goToNextPage={validateAndGoToNextTab}
          isSubmitting={submitting}
          showSchemeInfo={activeTab > DETAILS_TAB}
          // calculatedPremiums={premiums || {}}
          isSaving={isSaving}
          // isCalculating={isCalculating}
          // isPremiumAdjusting={isPremiumAdjusting}
          // calculateQuotes={calculateQuotes}
          // currentProduct={currentProduct}
          handleCommissionAdjustments={handleCommissionAdjustments}
          calculateQuotes={calculateQuotes}
          // // handleGrossOverrode={handleGrossOverrode}
          // goToNextPage={handleNextTab}
          // valid={valid}
          // isMta={isMta}
          saveAndExit={handleSaveAndExit}
          saveEmailAndExit={handleEmailSaveAndExit}
        />
      </Form>
    </div>
  );
};

// const scrollToTop = () => {
//   const scroll = animateScroll;
//   scroll.scrollTo(1);
// };

export default compose(
  connect(
    (state, ownProps) => {
      return {
        initialValues: ownProps.resource,
        formValues: getFormValues(FORM_NAME)(state),
        formErrors: getFormSyncErrors(FORM_NAME)(state),
        valid: isValid(FORM_NAME)(state),
        channel: state.channel,
        customer: state.customer,
        updateBolton: state.boltons,
        selectMenus: state.selectMenus,
        channels: state.channel.channels,
        isCalculating: state.product.isCalculating,
      };
    },
    (dispatch) => ({
      channelActions: bindActionCreators(channelActions, dispatch),
      dispatch,
    }),
  ),
  reduxForm({ form: FORM_NAME }),
)(PersonalAccidentForm);
